import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState, useMemo, useEffect, useCallback } from "react";
import { Search, Plus, Phone, User, Copy, Check, ClipboardList } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Dialog, 
  DialogContent, 
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ClienteForm } from "@/components/ClienteForm";
import { supabase } from "@/lib/supabase";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";
import { useUserRole } from "@/hooks/useUserRole";

export const Route = createFileRoute("/clientes/")({
  component: ClientesPage,
});

// Tipagem para Clientes
interface Cliente {
  id: string;
  nome: string;
  telefone: string;
  email: string;
  instagram: string;
  data_nascimento: string;
  observacoes: string;
  total_gasto: number;
  status: "Ativo" | "Inativo";
  created_at: string;
}

function CardLinkFicha({ studioId }: { studioId: string }) {
  const [slug, setSlug] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    if (!studioId) return;
    let ativo = true;
    (async () => {
      const { data } = await supabase
        .from("configuracoes")
        .select("slug")
        .eq("studio_id", studioId)
        .maybeSingle();
      if (ativo) {
        setSlug(data?.slug ?? null);
        setCarregando(false);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [studioId]);

  if (carregando) return null;

  const linkFicha = slug ? `${window.location.origin}/ficha/${slug}` : null;

  async function copiarLink() {
    if (!linkFicha) return;
    try {
      await navigator.clipboard.writeText(linkFicha);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      toast.error("Não foi possível copiar o link");
    }
  }

  return (
    <Card className="bg-card border-border">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="bg-primary/10 p-2.5 rounded-lg text-primary shrink-0">
            <ClipboardList size={18} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">Link de cadastro e anamnese</p>
            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
              Envie esse link pro cliente preencher o cadastro e a ficha antes da tatuagem.
            </p>
          </div>
        </div>

        {linkFicha ? (
          <div className="flex items-center gap-2 mt-3">
            <div className="flex-1 min-w-0 bg-muted/50 border border-border rounded-lg px-3 py-2">
              <p className="text-xs text-muted-foreground truncate">{linkFicha}</p>
            </div>
            <Button
              type="button"
              size="sm"
              onClick={copiarLink}
              className={copiado ? "bg-foreground text-background hover:bg-foreground/90" : "bg-primary hover:bg-primary/90 text-white"}
            >
              {copiado ? <Check size={14} className="mr-1.5" /> : <Copy size={14} className="mr-1.5" />}
              {copiado ? "Copiado" : "Copiar"}
            </Button>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground mt-3">
            Defina o link do seu estúdio em{" "}
            <Link to="/configuracoes" className="text-primary underline underline-offset-2">
              Configurações
            </Link>{" "}
            pra habilitar o cadastro público.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function ClientesPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { loading: roleLoading, isTatuador, userId, effectiveStudioId, permissaoClientes } = useUserRole();

  const fetchClientes = useCallback(async () => {
    if (!effectiveStudioId) return;
    try {
      const { data, error } = await supabase
        .from("clientes")
        .select("*")
        .eq("studio_id", effectiveStudioId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setClientes(data || []);
    } catch (error: any) {
      console.error("Erro ao buscar clientes:", error);
      toast.error("Erro ao carregar clientes");
    } finally {
      setIsLoading(false);
    }
  }, [effectiveStudioId]);

  useEffect(() => {
    if (!roleLoading) fetchClientes();
  }, [roleLoading, fetchClientes]);

  // Ativa o Realtime para a tabela clientes
  useRealtimeSubscription("clientes", fetchClientes);

  const filteredClientes = useMemo(() => {
    return clientes
      .filter(c => 
        c.nome.toLowerCase().includes(search.toLowerCase()) || 
        c.telefone.includes(search)
      );
  }, [search, clientes]);

  const handleSave = async (data: any) => {
    try {
      const { error } = await supabase
        .from("clientes")
        .insert({
          ...data,
          studio_id: effectiveStudioId,
          created_by: userId,
          total_gasto: 0,
          status: "Ativo",
        });
      
      if (error) throw error;
      
      toast.success("Cliente cadastrado com sucesso!");
      setIsDialogOpen(false);
      fetchClientes();
    } catch (error: any) {
      console.error("Erro ao salvar cliente:", error);
      toast.error("Erro ao salvar cliente: " + error.message);
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <CardLinkFicha studioId={effectiveStudioId} />

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-foreground">Clientes</h1>
          <p className="text-muted-foreground">
            {isTatuador && permissaoClientes === "proprios"
              ? "Clientes que você atende"
              : "Gerencie sua base de clientes"}
          </p>
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90 text-white w-full md:w-auto font-semibold h-11">
              <Plus className="mr-2 size-4" />
              Novo Cliente
            </Button>
          </DialogTrigger>
          <DialogContent className="p-0 border-none bg-transparent max-w-2xl shadow-none">
            <ClienteForm 
              title="Novo Cliente"
              onSubmit={handleSave}
              onCancel={() => setIsDialogOpen(false)}
            />
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
        <Input placeholder="Buscar por nome ou telefone..." className="pl-10 bg-card border-border" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {isLoading ? (
          <div className="col-span-full py-20 text-center">
            <div className="size-8 border-4 border-muted border-t-primary rounded-full animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Carregando clientes...</p>
          </div>
        ) : filteredClientes.map((cliente) => (
          <Card key={cliente.id} className="bg-card border-border hover:border-primary/50 transition-all cursor-pointer group" onClick={() => navigate({ to: `/clientes/${cliente.id}` })}>
            <CardContent className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="bg-primary/10 p-3 rounded-xl text-primary group-hover:bg-primary group-hover:text-white transition-colors">
                  <User size={24} />
                </div>
                <Badge className={cliente.status === "Ativo" ? "bg-green-500/10 text-green-500 border-green-500/20" : "bg-zinc-500/10 text-muted-foreground border-zinc-500/20"}>
                  {cliente.status}
                </Badge>
              </div>
              <div className="space-y-1">
                <h3 className="font-semibold text-lg text-foreground group-hover:text-primary transition-colors">{cliente.nome}</h3>
                <div className="flex items-center gap-2 text-sm text-muted-foreground"><Phone size={14} />{cliente.telefone}</div>
              </div>
              <div className="mt-6 pt-4 border-t border-border flex justify-between items-center">
                <span className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Total Gasto</span>
                <span className="text-primary font-semibold text-lg">{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cliente.total_gasto)}</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
